/**
 * SelectionTile — composite for single-choice interactive card selection.
 *
 * Renders a stateless, controlled tile using Card (asChild) and a native
 * <button aria-pressed>. The consumer manages selected state via onClick;
 * the component receives isSelected and maps it to aria-pressed. All
 * styling uses EDU Design System tokens.
 */

import * as React from 'react';
import { Card } from '../Card';
import { Heading, Icon, Text } from '@/hen-ui/atomics';
import { Flex, type FlexProps } from '@/hen-ui/composites/Flex';
import { IconName } from 'lucide-react/dynamic';
import { selectionTileVariants } from './selectiontile.variants';
import { cn } from '@/hen-ui/utils';

/** Props for SelectionTile. Extends native button attributes (onClick, disabled, className, etc.). */
export interface SelectionTileProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Primary tile text; rendered as Heading h5 (bold). */
  label: string;
  /** Optional secondary body text; rendered as Text b4, neutral. */
  description?: string;
  /** Optional Lucide icon name; rendered above the label as decorative Icon. */
  icon?: IconName;
  /** Controlled selected state; mapped to aria-pressed (no radio semantics). */
  isSelected: boolean;
  /** Content alignment: 'start' (default) or 'center'. */
  alignment?: 'start' | 'center';
  /** Main-axis alignment of the inner Flex (e.g. 'start', 'center', 'between'). */
  justify?: FlexProps['justify'];
  /** Border/background tint when selected: 'primary' | 'secondary'. */
  variant?: 'primary' | 'secondary';
}

/**
 * SelectionTile component. Ref forwards to the underlying button element.
 * Ref is set on the button (not Card) to avoid HTMLDivElement vs HTMLButtonElement type mismatch.
 */
export const SelectionTile = React.forwardRef<
  HTMLButtonElement,
  SelectionTileProps
>(
  (
    {
      label,
      description,
      icon,
      isSelected,
      alignment = 'start',
      variant = 'secondary',
      justify,
      className,
      ...buttonProps
    },
    ref,
  ) => (
    <Card asChild>
      <button
        ref={ref}
        type='button'
        aria-pressed={isSelected}
        className={cn(
          selectionTileVariants({
            variant,
            isSelected: isSelected ? 'yes' : 'no',
          }),
          className,
        )}
        {...buttonProps}
      >
        <Card.Content>
          <Flex
            direction='column'
            gap='xxs'
            align={alignment}
            justify={justify}
          >
            {/* Decorative icon above label; size md. */}
            {icon && <Icon icon={icon} size='md' aria-hidden />}
            {/* Label: opposite color to variant for contrast on tinted background. */}
            <Heading
              variant='h5'
              color={variant === 'primary' ? 'secondary' : 'primary'}
            >
              {label}
            </Heading>
            {description && (
              <Text
                variant='b4'
                align={alignment === 'start' ? 'left' : 'center'}
                weight='light'
                color='neutral'
              >
                {description}
              </Text>
            )}
          </Flex>
        </Card.Content>
      </button>
    </Card>
  ),
);

SelectionTile.displayName = 'SelectionTile';
